import React, { useCallback } from 'react'
import Link from 'next/link'

import { useForm } from 'react-hook-form'
import JSZip from 'jszip'
import FileSaver from 'file-saver'
import { getBasicConfig } from '../lib/apiClient'

export const MediaPlayerNetworkForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      getPlayerPackage: false,
      useStaticIP: false,
      ipAddress: '192.168.0.100',
      gateway: '192.168.0.1',
      netmask: '255.255.255.0',
      broadcast: '192.168.0.255',
      dns: '8.8.8.8',
      useWifi: false,
      wifiSSID: 'yourWiFiNetworkName',
      wifiPASS: 'your_supersecret_password',
    },
  })

  const getNetworkFile = useCallback(async (data) => {
    const {
      getPlayerPackage,
      ipAddress,
      netmask,
      gateway,
      broadcast,
      dns,
      useStaticIP,
      useWifi,
      wifiSSID,
      wifiPASS,
    } = data
    const networkConfig = {
      useStaticIP,
      ipAddress,
      netmask,
      gateway,
      broadcast,
      dns,
      useWifi,
      wifiSSID,
      wifiPASS,
    }
    const networkContent = JSON.stringify(networkConfig)
    if (getPlayerPackage) {
      const zip = new JSZip()
      const filenames = [
        'access-kit.png',
        'autorun.brs',
        'boot.brs',
        'syncPlayer.brs',
        'time.brs',
        'oscBuilder.brs',
        'oscParser.brs',
        'config.json',
        'init.json',
        'autozip.brs',
        'timeline.brs',
        'gpio.brs',
        'subtitler.brs',
        'subtitles.css',
        'subtitles.html',
      ]
      for (const filename of filenames) {
        const filecontent = await (
          await fetch(
            `https://raw.githubusercontent.com/szvsw/brightSignMediaSync/master/${filename}`
          )
        ).text()
        const dataToZip = filename === 'config.json'
          ? await (await getBasicConfig()).text()
          : filecontent
        zip.file(filename, dataToZip)
      }
      zip.file('network.json', networkContent)
      zip.generateAsync({ type: 'blob' }).then((content) => {
        FileSaver.saveAs(content, 'autorun.zip')
      })
    } else {
      const blob = new Blob([networkContent], { type: 'application/json' })
      FileSaver.saveAs(blob, 'network.json')
    }
  }, [])

  return (
    <div>
      <div className="form-header">
        <div>
          <h1>Create Mediaplayer Network Config File</h1>
        </div>
        <a href="https://docs.accesskit.cc/how-to/use-wifi-or-static-ips" target="_blank" rel="noreferrer">Help</a>
      </div>
      <form onSubmit={handleSubmit(getNetworkFile)}>
        <label htmlFor="useWifi" className="flex gap-2">
          <input type="checkbox" {...register('useWifi')} />
          <span>Use WiFi? (Check this if you will be using an onboard WiFi chip)</span>
          <p>{errors.useWifi?.message}</p>
        </label>
        {watch('useWifi') && (
        <>
          <label htmlFor="wifiSSID">
            WiFI SSID (Network Name)
            <input
              placeholder="yourWiFiNetworkName"
              type="text"
              {...register('wifiSSID', { required: 'WiFi SSID is required' })}
            />
            <p>{errors.wifiSSID?.message}</p>
          </label>
          <label htmlFor="wifiPASS">
            WiFi Password
            <input
              placeholder="your_supersecret_password"
              type="text"
              {...register('wifiPASS', { required: 'WiFi Password is required.' })}
            />
            <p>{errors.wifiPASS?.message}</p>
          </label>
        </>
        )}
        <label htmlFor="useStaticIP" className="flex gap-2">
          <input type="checkbox" {...register('useStaticIP')} />
          <span>Use Static IP? (if unchecked, DHCP will be used instead)</span>
          <p>{errors.useStaticIP?.message}</p>
        </label>
        {watch('useStaticIP') && (
        <>
          <label htmlFor="ipAddress">
            IP Address
            <input
              placeholder="192.168.0.1"
              type="text"
              {...register('ipAddress', { required: 'IP Address is required.' })}
            />
            <p>{errors.ipAddress?.message}</p>
          </label>
          <label htmlFor="Netmask">
            Netmask
            <input
              placeholder="255.255.255.0"
              type="text"
              {...register('netmask', { required: 'Netmask is required.' })}
            />
            <p>{errors.netmask?.message}</p>
          </label>
          <label htmlFor="broadcast">
            Broadcast Address
            <input
              placeholder="192.168.0.255"
              type="text"
              {...register('broadcast', {
                required: 'Broadcast Address is required.',
              })}
            />
            <p>{errors.broadcast?.message}</p>
          </label>
          <label htmlFor="gateway">
            Default Gateway
            <input
              placeholder="192.168.0.1"
              type="text"
              {...register('gateway', { required: 'Gateway is required.' })}
            />
            <p>{errors.gateway?.message}</p>
          </label>
          <label htmlFor="dns">
            DNS Server
            <input
              placeholder="8.8.8.8"
              type="text"
              {...register('dns', { required: 'DNS Server is required.' })}
            />
            <p>{errors.dns?.message}</p>
          </label>
        </>
        )}
        <label htmlFor="getPlayerPackage" className="flex gap-2">
          <input type="checkbox" {...register('getPlayerPackage')} />
          <span>
            Check to download entire player package (leave unchecked to just
            download network config file)
          </span>
          <p>{errors.getPlayerPackage?.message}</p>
        </label>
        <div className="form-submit">
          <input type="submit" value={`Get ${watch('getPlayerPackage') ? 'Player Package' : 'Network Config'}`} />
          <Link href="/admin">or Cancel</Link>
          <p className="form-note mt-4">Place the network.json file on the mediaplayer's SD card and restart the mediaplayer for it to connect automatically.</p>
        </div>
      </form>
    </div>
  )
}
